/**
 * Utility functions for handling sorting operations
 */

export type SortOrder = 1 | -1 | "asc" | "desc";

/**
 * Converts any sort order value to the standardized numeric format (1 or -1)
 * Matches the server DTO's @Transform logic
 */
export const normalizeSortOrder = (
  value: SortOrder | string | number
): 1 | -1 => {
  if (value === "asc" || value === "1" || value === 1) return 1;
  if (value === "desc" || value === "-1" || value === -1) return -1;
  return 1; // default to ascending
};

/**
 * Gets the display arrow for the current sort order
 */
export const getSortArrow = (
  sortOrder: SortOrder | string | number
): string => {
  return normalizeSortOrder(sortOrder) === 1 ? "↑" : "↓";
};

/**
 * Toggles between ascending and descending sort order
 */
export const toggleSortOrder = (
  currentOrder: SortOrder | string | number
): 1 | -1 => {
  return normalizeSortOrder(currentOrder) === 1 ? -1 : 1;
};

/**
 * Parses a sort string in the format "field1:order1,field2:order2"
 * Returns an array of sort objects
 */
export const parseSortString = (
  sortString: string
): { sortBy: string; sortOrder: 1 | -1 }[] => {
  if (!sortString) return [];

  return sortString.split(",").map((part) => {
    const [field, order] = part.trim().split(":");
    return {
      sortBy: field,
      sortOrder: normalizeSortOrder(order || "asc"),
    };
  });
};

/**
 * Converts an array of sort objects to a sort string
 * Format: "field1:order1,field2:order2"
 */
export const buildSortString = (
  sorts: { sortBy: string; sortOrder: 1 | -1 }[]
): string => {
  if (!sorts || sorts.length === 0) return "";

  return sorts
    .map((sort) => `${sort.sortBy}:${sort.sortOrder === 1 ? "asc" : "desc"}`)
    .join(",");
};
